// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/verbaEmMassa.ts

/**
 * O modal que aplica verba de sell-in a vários itens de uma vez. **Os textos são literais do
 * contrato visual**, e nenhuma conta mora aqui: quanto cada item recebe e a conversão do custo de
 * entrada para o líquido são do motor, item a item.
 */

import { aplicarDerivadosDaGravacao } from './derivadosDaGravacao';
import type { PatchDaGravacao } from './derivadosDaGravacao';
import { SEM_EDICAO, marcarVerbaComoGravada } from './verbaDeSellIn';
import type { EdicaoDaVerba, MotivoSemSugestao, OrigemDaVerba } from './verbaDeSellIn';

export type ModoDaMassa = 'IDEAL_PER_ITEM' | 'SAME_VALUE_FOR_ALL';

/** Os motivos vêm importados da célula da linha, para os dois conjuntos não divergirem. */
export type MotivoDaMassa = MotivoSemSugestao | 'REACHES_NEW_NET_COST' | 'NEGATIVE_VALUE' | 'SUGGESTION_NOT_ASSESSED';

/** **Sem os dois últimos a dica do novo custo não desenha a cadeia da verba.** */
export type ItemAplicadoNaMassa = {
  /** Vazio na visão rede, que agrega por produto e não tem linha. Ver {@link identidadeDoItemDaMassa}. */
  id?: string | null;
  productId?: string | null;
  sellinAllowance?: number | null;
  sellinAllowanceOrigin?: OrigemDaVerba | null;
  /** O custo líquido com a verba já abatida, derivado pelo motor nesta gravação. */
  effectiveNetCost?: number | null;
  /** A mesma verba no custo de entrada, que é o que o comprador pede na nota. */
  sellinAllowanceToSupplier?: number | null;
};

/** O código e a descrição vêm na resposta porque o recusado pode não estar na página em memória. */
export type ItemRecusadoNaMassa = {
  /** Vazio na visão rede, como em {@link ItemAplicadoNaMassa}. */
  id?: string | null;
  productId?: string | null;
  description?: string | null;
  sellinAllowance?: number | null;
  /** O novo custo líquido do item. É contra ele que a verba foi comparada. */
  newNetCost?: number | null;
  reason?: MotivoDaMassa | null;
};

/** A tela não o desenha: existe para a decisão ser auditável. */
export type ItemNaoTocadoNaMassa = {
  /** Vazio na visão rede, como em {@link ItemAplicadoNaMassa}. */
  id?: string | null;
  productId?: string | null;
  reason?: MotivoDaMassa | null;
};

/** O identificador de linha manda; nos grãos loja e cluster o produto aparece em muitas linhas. */
export const identidadeDoItemDaMassa = (item: { id?: string | null; productId?: string | null }): string | null => {
  const identificadorDaLinha = (item.id ?? '').trim();

  if (identificadorDaLinha !== '') return identificadorDaLinha;

  const codigoDoProduto = (item.productId ?? '').trim();

  return codigoDoProduto !== '' ? codigoDoProduto : null;
};

export type ResultadoDaMassa = {
  applied?: ItemAplicadoNaMassa[] | null;
  rejected?: ItemRecusadoNaMassa[] | null;
  untouched?: ItemNaoTocadoNaMassa[] | null;
};

/** `textoDoValor` nulo é **campo inexistente**, e não campo vazio: o modo ideal não pede valor. */
export type EstadoDaMassa = {
  modo: ModoDaMassa;
  textoDoValor: string | null;
  resultado: ResultadoDaMassa | null;
};

/** **A parte que as duas telas compartilham**; o corpo em volta dela difere entre elas. */
export type EscolhaDaMassa = {
  mode: ModoDaMassa;
  allowancePerUnit?: number;
};

/** O corpo do pedido. O inquilino e o autor NÃO viajam daqui: o gateway os resolve pelo token. */
export type PedidoDaMassa = EscolhaDaMassa & {
  datapointsIds: string[];
};

export const TEXTO_DA_MASSA = {
  TITULO: 'Adicionar verba de sell-in',
  OPCAO_IDEAL: 'Aceitar a verba ideal de cada item',
  DESCRICAO_IDEAL: 'Cada produto recebe o valor que recompõe sua margem objetiva praticando o preço do mercado.',
  OPCAO_MESMO_VALOR: 'Aplicar o mesmo valor a todos',
  DESCRICAO_MESMO_VALOR: 'Use quando o fornecedor fechou um valor único para o pedido.',
  ROTULO_DO_CAMPO: 'Verba por unidade — no custo de entrada',
  EXEMPLO_DO_CAMPO: '0,30',
  PREFIXO_DO_CAMPO: 'R$',
  AVISO_DA_CONVERSAO:
    'É o valor que o fornecedor concede na nota. O sistema converte para o seu custo líquido em cada item, conforme a alíquota de entrada.',
  AVISO_DA_IDEAL: 'Produtos sem preço de concorrência não recebem sugestão — a verba neles fica em branco.',
  ERRO_DE_VALOR: 'Valor inválido — a verba precisa ser um número positivo em reais (ex.: 0,30).',
  FECHAR: 'FECHAR',
  APLICAR: 'APLICAR',
  ROTULO_DO_BOTAO: '+ ADICIONAR VERBA',
  DICA_DO_BOTAO: 'Adicionar verba de sell-in aos itens selecionados',
  SUCESSO_DA_IDEAL: 'Verba ideal aplicada aos selecionados.',
  SUCESSO_DO_MESMO_VALOR: 'Verba aplicada e convertida para o seu custo líquido.',
} as const;

/** As duas telas abrem o MESMO modal; escrita à mão em dois lugares, a chave diverge calada. */
export const CHAVE_DO_MODAL_DA_MASSA = 'adicionarVerbaEmMassa';

export const ESTADO_INICIAL_DA_MASSA: EstadoDaMassa = { modo: 'IDEAL_PER_ITEM', textoDoValor: null, resultado: null };

export const temCampoDeValor = (estado: EstadoDaMassa): boolean => estado.textoDoValor !== null;

/** Vírgula e ponto como separador decimal: um é o que ele digita, o outro o teclado numérico. */
const PADRAO_NUMERICO = /^-?\d+(?:[.,]\d+)?$/;

const numeroDoTexto = (texto: string): number | null => {
  const limpo = texto.trim();

  if (!PADRAO_NUMERICO.test(limpo)) return null;

  const numero = Number(limpo.replace(',', '.'));

  return Number.isFinite(numero) ? numero : null;
};

/** Trocar de modo **esquece o valor digitado e o resultado anterior**: são do modo que saiu. */
export const escolherOModoDaMassa = (estado: EstadoDaMassa, modo: ModoDaMassa): EstadoDaMassa => {
  // Reafirmar o modo já escolhido não é troca, e não pode apagar o campo pelo caminho.
  if (modo === estado.modo) return estado;

  return { modo, textoDoValor: modo === 'SAME_VALUE_FOR_ALL' ? '' : null, resultado: null };
};

export const digitarOValorDaMassa = (estado: EstadoDaMassa, texto: string): EstadoDaMassa => {
  if (!temCampoDeValor(estado)) return estado;

  return { ...estado, textoDoValor: texto };
};

export const registrarOResultadoDaMassa = (estado: EstadoDaMassa, resultado: ResultadoDaMassa): EstadoDaMassa => ({ ...estado, resultado });

/** **Campo vazio não é erro**, só mantém o aplicar desabilitado. Zero é inválido. */
export const erroDoCampoDaMassa = (estado: EstadoDaMassa): string | null => {
  if (!temCampoDeValor(estado)) return null;

  const texto = (estado.textoDoValor ?? '').trim();

  if (texto === '') return null;

  const numero = numeroDoTexto(texto);

  if (numero === null || numero <= 0) return TEXTO_DA_MASSA.ERRO_DE_VALOR;

  return null;
};

export const podeAplicarAMassa = (estado: EstadoDaMassa): boolean => {
  if (!temCampoDeValor(estado)) return true;

  return (estado.textoDoValor ?? '').trim() !== '' && erroDoCampoDaMassa(estado) === null;
};

/** Nada é a decisão de não chamar. **A seleção não entra:** quem a conhece é a tela. */
export const escolhaDaMassa = (estado: EstadoDaMassa): EscolhaDaMassa | null => {
  if (!podeAplicarAMassa(estado)) return null;

  if (estado.modo === 'IDEAL_PER_ITEM') return { mode: estado.modo };

  const valor = numeroDoTexto(estado.textoDoValor ?? '');

  if (valor === null) return null;

  return { mode: estado.modo, allowancePerUnit: valor };
};

export const subtituloDaMassa = (quantidade: number): string =>
  quantidade === 1 ? 'Aplicar a 1 produto selecionado' : `Aplicar a ${quantidade} produtos selecionados`;

export const linhaDosAplicadosDaMassa = (quantidade: number): string => `Verba aplicada a ${quantidade} produto(s).`;

const FIM_DA_LINHA_DA_RECUSA =
  'a verba, convertida para o custo líquido, ficaria maior ou igual ao novo custo — o item permaneceu como estava.';

export const linhaDaRecusaDaMassa = (quantidade: number): string =>
  quantidade === 1 ? `1 item foi recusado: ${FIM_DA_LINHA_DA_RECUSA}` : `${quantidade} itens foram recusados: ${FIM_DA_LINHA_DA_RECUSA}`;

export const houveRecusaNaMassa = (resultado: ResultadoDaMassa | null): boolean => (resultado?.rejected?.length ?? 0) > 0;

/**
 * A massa **não escreve estado de célula por conta própria**. Item sem origem não produz edição:
 * origem vazia é a limpeza do acordo.
 */
export const edicoesDaMassaAplicada = (resultado: ResultadoDaMassa | null): Record<string, EdicaoDaVerba> => {
  const aplicados = resultado?.applied ?? [];

  return aplicados.reduce<Record<string, EdicaoDaVerba>>((acumulado, item) => {
    const identidade = identidadeDoItemDaMassa(item);

    if (identidade === null) return acumulado;

    // O item INTEIRO: recortar chaves a dedo deixa de fora os números de que a dica depende.
    const edicao = marcarVerbaComoGravada(SEM_EDICAO, item);

    if (edicao.origem === null) return acumulado;

    return { ...acumulado, [identidade]: edicao };
  }, {});
};

/**
 * **A célula sozinha não basta:** o custo líquido efetivo e a verba ao fornecedor saem da LINHA,
 * e é deles que a dica depende. Mesma porta, e mesma regra, da gravação de uma linha.
 */
export const linhasDaMassaAplicada = (resultado: ResultadoDaMassa | null): Record<string, PatchDaGravacao> => {
  const aplicados = resultado?.applied ?? [];

  return aplicados.reduce<Record<string, PatchDaGravacao>>((acumulado, item) => {
    const identidade = identidadeDoItemDaMassa(item);

    if (identidade === null) return acumulado;

    return { ...acumulado, [identidade]: aplicarDerivadosDaGravacao(item) };
  }, {});
};
